import React, { useState, useEffect } from 'react';
import {
  getSavedQueries,
  createSavedQuery,
  updateSavedQuery,
  deleteSavedQuery,
  executeMultiTenantQuery,
} from '../../services/queryVaultApi';
import { fetchTenants } from '../../services/api';
import SavedQueriesList from './SavedQueriesList';
import QueryEditor from './QueryEditor';
import MultiTenantResults from './MultiTenantResults';
import { X } from 'lucide-react';

const QueryVault = () => {
  const [queries, setQueries] = useState([]);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [queryText, setQueryText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState(null);
  const [executeError, setExecuteError] = useState(null);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveForm, setSaveForm] = useState({ title: '', description: '' });
  const [tenants, setTenants] = useState([]);

  // ── Tenant & results state ──────────────────────────────────
  const [tenantMode, setTenantMode] = useState('select'); // 'select' | 'all'
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [multiResults, setMultiResults] = useState(null);

  useEffect(() => { loadQueries(); }, []);

  useEffect(() => {
    const loadTenants = async () => {
      try {
        const tenantList = await fetchTenants();
        setTenants(tenantList);
      } catch {
        setTenants([]);
      }
    };
    loadTenants();
  }, []);

  useEffect(() => {
    if (selectedQuery) {
      setQueryText(selectedQuery.queryText || selectedQuery.QueryText || '');
      // Pre-select the saved query's tenant
      const savedTenant = selectedQuery.tenantName || selectedQuery.TenantName || '';
      if (savedTenant && savedTenant !== '*') {
        setSelectedTenants([savedTenant]);
        setTenantMode('select');
      } else if (savedTenant === '*') {
        setTenantMode('all');
      }
    } else {
      setQueryText('');
    }
  }, [selectedQuery]);

  // Clear results when switching modes
  useEffect(() => {
    setMultiResults(null);
    setExecuteError(null);
  }, [tenantMode]);

  const loadQueries = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getSavedQueries();
      setQueries(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load queries');
      setQueries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectQuery = (query) => {
    setSelectedQuery(query);
    setMultiResults(null);
    setError(null);
    setExecuteError(null);
  };

  const handleAddNew = () => {
    setSelectedQuery(null);
    setQueryText('');
    setMultiResults(null);
    setSaveForm({ title: '', description: '' });
  };

  const handleDelete = async (id) => {
    try {
      await deleteSavedQuery(id);
      if (selectedQuery && (selectedQuery.id === id || selectedQuery.Id === id)) {
        setSelectedQuery(null);
        setQueryText('');
      }
      await loadQueries();
    } catch (err) {
      setError(err.message || 'Failed to delete query');
    }
  };

  const handleExecute = async () => {
    if (!queryText.trim()) return;

    const tenantsToQuery = tenantMode === 'all' ? ['*'] : selectedTenants;
    if (tenantsToQuery.length === 0) return;

    setExecuting(true);
    setExecuteError(null);
    setMultiResults(null);

    try {
      const data = await executeMultiTenantQuery({
        queryText: queryText.trim(),
        tenants: tenantsToQuery,
      });
      setMultiResults(data);
    } catch (err) {
      setExecuteError(err.details?.message || err.details?.error || err.message || 'Failed to execute query');
    } finally {
      setExecuting(false);
    }
  };

  const handleSaveClick = () => {
    if (selectedQuery) {
      handleUpdate();
    } else {
      setSaveModalOpen(true);
    }
  };

  const handleUpdate = async () => {
    if (!selectedQuery || !queryText.trim()) return;
    const saveTenant = tenantMode === 'all' ? '*' : (selectedTenants[0] || '*');
    try {
      await updateSavedQuery(selectedQuery.id || selectedQuery.Id, {
        title: selectedQuery.title || selectedQuery.Title,
        description: selectedQuery.description || selectedQuery.Description,
        queryText: queryText.trim(),
        tenantName: saveTenant,
      });
      await loadQueries();
      setSelectedQuery((prev) =>
        prev ? { ...prev, queryText: queryText.trim(), tenantName: saveTenant } : null
      );
    } catch (err) {
      setError(err.message || 'Failed to update query');
    }
  };

  const handleSaveSubmit = async (e) => {
    e.preventDefault();
    if (!saveForm.title.trim() || !queryText.trim()) return;
    const saveTenant = tenantMode === 'all' ? '*' : (selectedTenants[0] || '*');
    try {
      await createSavedQuery({
        title: saveForm.title.trim(),
        description: saveForm.description?.trim() || null,
        queryText: queryText.trim(),
        tenantName: saveTenant,
      });
      setSaveModalOpen(false);
      setSaveForm({ title: '', description: '' });
      await loadQueries();
    } catch (err) {
      setError(err.message || 'Failed to save query');
    }
  };

  return (
    <div className="p-6 h-[calc(100vh-64px)] flex flex-col overflow-hidden">
      {error && (
        <div className="mb-4 flex items-center justify-between p-3 bg-red-50 dark:bg-red-950 text-red-700 rounded-lg">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="px-3 py-1 bg-red-100 hover:bg-red-200 rounded font-medium">
            Dismiss
          </button>
        </div>
      )}

      <div className="flex gap-6 h-full flex-1 min-h-0 min-w-0 overflow-hidden">
        <div className="w-80 flex flex-col bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm overflow-hidden shrink-0">
          <SavedQueriesList
            queries={queries}
            selectedQuery={selectedQuery}
            onSelect={handleSelectQuery}
            onDelete={handleDelete}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onAddNew={handleAddNew}
            loading={loading}
          />
        </div>

        <div className="flex-1 flex flex-col bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm overflow-hidden min-w-0">
          <QueryEditor
            queryText={queryText}
            setQueryText={setQueryText}
            tenantMode={tenantMode}
            setTenantMode={setTenantMode}
            selectedTenants={selectedTenants}
            setSelectedTenants={setSelectedTenants}
            tenants={tenants}
            onExecute={handleExecute}
            onSave={handleSaveClick}
            executing={executing}
          />
          <MultiTenantResults
            multiResults={multiResults}
            loading={executing}
            error={executeError}
          />
        </div>
      </div>

      {saveModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4"
          onClick={() => setSaveModalOpen(false)}
        >
          <div
            className="bg-white dark:bg-slate-900 rounded-xl shadow-xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Save Query</h3>
              <button onClick={() => setSaveModalOpen(false)} className="p-1 text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-gray-300">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSaveSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title *</label>
                <input
                  type="text"
                  value={saveForm.title}
                  onChange={(e) => setSaveForm((p) => ({ ...p, title: e.target.value }))}
                  placeholder="Query title"
                  required
                  className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none dark:bg-slate-800 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <textarea
                  value={saveForm.description}
                  onChange={(e) => setSaveForm((p) => ({ ...p, description: e.target.value }))}
                  placeholder="What does this query do?"
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-y dark:bg-slate-800 dark:text-gray-100"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  type="submit"
                  disabled={!saveForm.title.trim()}
                  className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-60"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setSaveModalOpen(false)}
                  className="px-4 py-2 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default QueryVault;
